import { useEffect, useRef } from 'react';
import { ImageOff } from 'lucide-react';
import { FLAG_H, FLAG_PALETTE, FLAG_W, decodeFlag, hexToRgb } from '../constants';
import ws from '../WorldsPage.module.scss';

/** Rendu d'un drapeau (base64 de 338 index de palette) dans un canvas 26x13 agrandi sans lissage. */
export function drawFlag(canvas: HTMLCanvasElement, pixels: Uint8Array): void {
  canvas.width = FLAG_W;
  canvas.height = FLAG_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const img = ctx.createImageData(FLAG_W, FLAG_H);
  const rgb = FLAG_PALETTE.map(hexToRgb);
  for (let i = 0; i < pixels.length; i++) {
    const [r, g, b] = rgb[pixels[i]] ?? rgb[12];
    img.data[i * 4] = r;
    img.data[i * 4 + 1] = g;
    img.data[i * 4 + 2] = b;
    img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
}

/**
 * Vignette d'un drapeau, rapport 2:1. Tailles : 20 dans les listes denses, 48 dans les tables,
 * 64 dans la fiche du pays actif, 40 dans le titre d'une modale. Sans drapeau (null) : cadre
 * pointillé de même taille avec ImageOff (`showEmpty`, défaut) ou simple espace réservé (listes
 * denses, `showEmpty={false}`), jamais un faux drapeau blanc.
 */
export function FlagThumb({ flag, width = 48, showEmpty = true }: { flag: string | null; width?: number; showEmpty?: boolean }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const height = Math.round((width * FLAG_H) / FLAG_W);
  useEffect(() => {
    if (flag != null && ref.current) drawFlag(ref.current, decodeFlag(flag));
  }, [flag]);

  if (flag == null) {
    if (!showEmpty) return <span className={ws.flagThumbSpacer} style={{ width, height }} aria-hidden />;
    return (
      <span className={ws.flagThumbEmpty} style={{ width, height }} title="Aucun drapeau défini : blanc en jeu"
        role="img" aria-label="Aucun drapeau défini">
        <ImageOff size={Math.max(10, Math.round(width / 4))} aria-hidden />
      </span>
    );
  }
  return <canvas ref={ref} className={ws.flagThumb} style={{ width, height }} role="img" aria-label="Drapeau" />;
}
